import { api } from '../api';
import { loadColor, svgPath } from '../format';
import { useApp } from '../store';
import { btn, divider, mono, head, panel, panelPad, sectionTitle, secondary } from '../ui';

const TEMP_MAX = 110;

export function Detail() {
  const app = useApp();
  const host = app.detailId ? app.hostById(app.detailId) : undefined;

  if (!host) {
    return (
      <section style={{ ...panelPad, fontSize: 12.5, color: 'var(--ink3)' }}>
        Хост не найден — возможно, агент снят с учёта.{' '}
        <button onClick={() => app.go('fleet')} style={{ ...btn, color: 'var(--acc)' }}>
          Вернуться к списку
        </button>
      </section>
    );
  }

  // Только та история, которую сервер действительно накопил. Пока измерений нет,
  // график остаётся пустым — подпись рядом честно говорит «нет истории».
  const points = app.detailHistory.length >= 2 ? app.detailHistory.slice(-36) : [];
  const cpuSeries = points.map(p => p.cpu);
  const tempSeries = points.map(p => p.temp);

  const thresholdY = (180 - (82 / TEMP_MAX) * 180).toFixed(1);

  const stats = [
    { label: 'CPU', value: host.cpu + '%', note: 'загрузка процессора', color: loadColor(host.cpu) },
    {
      label: 'Температура',
      value: host.temp ? host.temp + '°C' : '—',
      note: host.temp ? 'порог 82°' : 'датчик недоступен',
      color: host.temp > 82 ? 'var(--bad)' : 'var(--ink)'
    },
    { label: 'RAM', value: host.ram + '%', note: 'занято оперативной памяти', color: 'var(--ink)' },
    {
      label: 'Канал',
      value: host.down + ' Мбит',
      note: `↑${host.up} · ping ${host.ping} мс`,
      color: 'var(--ink)'
    }
  ];

  const disks = host.disks.length
    ? host.disks.map(d => ({
        label: d.letter,
        w: d.usedPct + '%',
        note: `${d.usedPct}% из ${d.totalGb} ГБ`,
        color: d.usedPct > 88 ? 'var(--bad)' : 'var(--acc)'
      }))
    : [
        {
          label: 'C:',
          w: host.disk + '%',
          note: host.disk + '% занято',
          color: host.disk > 88 ? 'var(--bad)' : 'var(--acc)'
        }
      ];

  const channel = {
    label: 'Канал',
    w: Math.min(100, Math.round((host.down / 1000) * 100)) + '%',
    note: host.down + ' Мбит/с',
    color: 'var(--ok)'
  };

  const services = host.services.length
    ? host.services
    : [{ name: 'FleetAgent', state: host.state === 'offline' ? 'Stopped' : 'Running', memMb: -1 }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ ...panel, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, padding: 20 }}>
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            onClick={() => app.go('fleet')}
            style={{ ...btn, fontSize: 11.5, color: 'var(--acc)', width: 'max-content' }}
          >
            ← Все серверы
          </button>
          <span style={{ ...mono, fontSize: 21, fontWeight: 500, letterSpacing: '-0.01em' }}>{host.name}</span>
          <span style={{ ...mono, fontSize: 11.5, color: 'var(--ink3)' }}>
            {host.ip} · {host.os} · uptime {host.uptime} · {host.loc}
            {host.simulated ? ' · демо' : ` · агент ${host.agentVersion}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            className="bright"
            href={api.rdpUrl(host.id)}
            style={{
              padding: '9px 15px',
              borderRadius: 'var(--rs)',
              background: 'var(--acc)',
              color: 'var(--accInk)',
              fontSize: 12.5,
              fontWeight: 600,
              textDecoration: 'none'
            }}
          >
            Открыть RDP
          </a>
          <button
            className="hov"
            style={secondary}
            onClick={() => {
              app.selectIds([host.id]);
              app.go('commands');
            }}
          >
            Команда
          </button>
          <button
            className="hov"
            style={secondary}
            onClick={() => {
              app.selectIds([host.id]);
              app.go('files');
            }}
          >
            Файлы
          </button>
          <button
            className="hov"
            style={secondary}
            onClick={() => {
              app.selectIds([host.id]);
              void app.runSpeed();
            }}
          >
            Speedtest
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14 }}>
        {stats.map(s => (
          <div
            key={s.label}
            style={{ ...panel, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{s.label}</span>
            <span
              style={{
                ...head,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                color: s.color
              }}
            >
              {s.value}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{s.note}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 14 }}>
        <section style={panelPad}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
            <span style={sectionTitle}>CPU и температура</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
              {points.length ? `${points.length} измерений` : 'нет истории'}
            </span>
          </div>
          <svg viewBox="0 0 520 180" preserveAspectRatio="none" style={{ width: '100%', height: 180, display: 'block' }}>
            <g stroke="var(--grid)" strokeWidth="1">
              <line x1="0" y1="45" x2="520" y2="45" />
              <line x1="0" y1="90" x2="520" y2="90" />
              <line x1="0" y1="135" x2="520" y2="135" />
            </g>
            <path d={`M0 ${thresholdY} L520 ${thresholdY}`} stroke="var(--bad)" strokeWidth="1" strokeDasharray="5 5" fill="none" />
            <path d={svgPath(cpuSeries, 520, 180, 100)} fill="none" stroke="var(--acc)" strokeWidth="2" strokeLinejoin="round" />
            <path d={svgPath(tempSeries, 520, 180, TEMP_MAX)} fill="none" stroke="var(--warn)" strokeWidth="2" strokeLinejoin="round" />
          </svg>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, fontSize: 11.5, color: 'var(--ink3)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 16, height: 2, background: 'var(--acc)' }} />
              CPU %
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 16, height: 2, background: 'var(--warn)' }} />
              °C ядро
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 16, height: 0, borderTop: '1px dashed var(--bad)' }} />
              порог 82°
            </span>
          </div>
        </section>

        <section style={{ ...panelPad, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={sectionTitle}>Диски и канал</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...disks, channel].map(d => (
              <div
                key={d.label}
                style={{ display: 'grid', gridTemplateColumns: '44px 1fr 96px', gap: 12, alignItems: 'center' }}
              >
                <span style={{ ...mono, fontSize: 12 }}>{d.label}</span>
                <span style={{ display: 'block', height: 7, borderRadius: 99, background: 'var(--track)' }}>
                  <span style={{ display: 'block', height: 7, borderRadius: 99, background: d.color, width: d.w }} />
                </span>
                <span style={{ ...mono, fontSize: 11, color: 'var(--ink3)', textAlign: 'right' }}>{d.note}</span>
              </div>
            ))}
          </div>
          <div style={divider} />
          <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Службы под наблюдением</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {services.map(s => (
              <div
                key={s.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 12,
                  ...mono,
                  fontSize: 11.5,
                  alignItems: 'baseline'
                }}
              >
                <span>{s.name}</span>
                <span style={{ color: serviceColor(s.state) }}>{s.state}</span>
                <span style={{ color: 'var(--ink3)' }}>{s.memMb >= 0 ? formatMem(s.memMb) : '—'}</span>
              </div>
            ))}
          </div>

          {host.sessions.length > 0 && (
            <>
              <div style={divider} />
              <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Сессии RDP</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {host.sessions.map(s => (
                  <div
                    key={s.sessionId + s.user}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 12,
                      ...mono,
                      fontSize: 11.5,
                      alignItems: 'baseline'
                    }}
                  >
                    <span>{s.user}</span>
                    <span style={{ color: s.state === 'Active' ? 'var(--ok)' : 'var(--ink3)' }}>{s.state}</span>
                    <span style={{ color: 'var(--ink3)' }}>{s.idle}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function serviceColor(state: string): string {
  if (state === 'Running') return 'var(--ok)';
  if (state === 'Stopped') return 'var(--bad)';
  return 'var(--warn)';
}

function formatMem(mb: number): string {
  return mb >= 1024 ? (mb / 1024).toFixed(1) + ' ГБ' : mb + ' МБ';
}
